import { DocumentTypeEnum, PatientDetailsFormFields, Resource, SignatureFileEntry } from 'common';
import { FullScreenSignDialogComponent } from '../../../full-screen-sign-dialog.component';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { SharedApiService } from '../../../../../services/shared-api.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FileService } from '../../../../../services/file.service';
import { Subscription, catchError, startWith, take } from 'rxjs';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl, FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgClass } from '@angular/common';
import dayjs from 'dayjs';


@Component({
  selector: 'lib-patient-signature-pages',
  standalone: true,
  imports: [MatCheckboxModule, MatButtonModule, MatIconModule, FormsModule, NgClass],
  template: `
    <mat-checkbox color="primary" class="my-3" 
      [disabled]="disabled"
      [(ngModel)]="agreed"
      (change)="onAgree($event)"
    >
      I hereby agree to be bound by the terms and conditions of the agreement between the customer,
      practitioner and Specsfin insofar as they are relevant to me.
    </mat-checkbox>

    <!-- Download Form Button -->
    <button mat-raised-button class="my-2 pos-right"
      [disabled]="isDownloading"
      (click)="downloadPatientAgreementForm()">Patient Agreement Form <mat-icon>download</mat-icon>
    </button>

    @if(agreed) {
      <div class="my-2">
        @if(signatureFile) {
          <img [src]="signatureFile.base64 || signatureFile.serverUrl" alt="Signature" class="signature-border">
        }
        <div class="d-flex">

          <button mat-raised-button color="primary" (click)="openDialog()"
            [disabled]="disabled || signatureFile?.isUploading"
          ><mat-icon>edit</mat-icon>{{signatureFile ? 'Update' : 'Sign here'}}</button>

          @if (signatureFile?.isFailed) {
            <span class="flex-center px-3 error-text">Error uploading signature. Please try again.</span>
            <button mat-raised-button color="warn" (click)="uploadSignatureToServer()" [disabled]="signatureFile?.isUploading">Try again <mat-icon iconPositionEnd>warning</mat-icon></button>
          }
        </div>
      </div>
    }
  `,
  styles: `

  mat-checkbox {
    display: block;
  }

  .pos-right {
    float: right;
  }

  @media (max-width: 600px) {
    .pos-right {
      float: none;
    }
  }

  img {
    margin-bottom: 0.5rem;
  }

  .error-text{
    color: crimson;
    background: rgb(237 20 61 / 10%);
  }

  .signature-border {
    border: 1px solid #000;
    background: #ccc;
    object-fit: contain;
    max-width: 100%;
    max-height: 200px;
    display: block;
  }

  ::ng-deep {
    .dark-theme {
      .signature-border {
        border: 2px solid rgb(233 233 233 / 42%) !important;
        background-color: #878787;
      }
    }
  }
  `,
})
export class PatientSignaturePagesComponent implements OnChanges {

  @Input()
  public disabled = false;

  @Input({ required: true })
  public vendorId?: number | null;

  @Input({ required: true })
  public patientControl!: FormControl<PatientDetailsFormFields | null>;

  public signatureFile: SignatureFileEntry | null = null;
  private formChangesSub: Subscription | null = null;
  public agreed = false;
  public isDownloading = false;

  private apiService = inject(SharedApiService);
  private fileService = inject(FileService);
  public snackbar = inject(MatSnackBar);
  public dialog = inject(MatDialog);

  public ngOnChanges(changes: SimpleChanges): void {

    // Monitor form and sync changes to the signature file.
    if (changes['patientControl']) {
      if (this.formChangesSub) {
        this.formChangesSub.unsubscribe();
      }

      this.formChangesSub = this.patientControl.valueChanges
        .pipe(startWith(null), take(2))
        .subscribe(() => {
          const resource = this.patientControl.get('signaturePatient')?.value as Resource | null;

          if (resource) {
            this.agreed = true;
            this.signatureFile = {
              base64: undefined,
              filename: resource.name,
              serverUrl: resource.contentUrl,
              isUploadedToServer: true,
            };
          };
        });
    }
  }

  public onAgree($event: MatCheckboxChange): void {
    // If the patient does not agree, remove the signature.
    if (!$event.checked) {
      const previousHadSignature = this.patientControl.get('signaturePatientId')?.value !== null;

      this.patientControl.get('signaturePatientId')?.setValue(null);
      this.patientControl.get('signaturePatient')?.setValue(null);
      this.signatureFile = null;

      // Mark as touched.
      this.patientControl.get('signaturePatientId')?.markAsTouched();
      this.patientControl.get('signaturePatient')?.markAsTouched();

      if (previousHadSignature) {
        this.snackbar.open('Signature removed.', 'Close', { duration: 3000 });
      }
    }
  }

  public openDialog() {
    const dialogRef = this.dialog.open(FullScreenSignDialogComponent, {
      height: '95%',
      width: '95%',
      maxHeight: 'unset',
      maxWidth: 'unset',
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe(base64 => {

        if (!base64) {
          this.patientControl.get('signaturePatientId')?.setValue(null);
          this.patientControl.get('signaturePatient')?.setValue(null);
        } else {
          this.signatureFile = {
            base64,
            filename: this.toFilename(),
            isUploadedToServer: false,
          };
          this.uploadSignatureToServer();
        }
      });
  }

  public downloadPatientAgreementForm() {
    if (!this.vendorId) return;
    this.isDownloading = true;

    this.apiService.vendorApplicationCustomerDocument(DocumentTypeEnum.PatientAgreement, this.vendorId)
      .pipe(catchError(error => {
        console.error('Failed to download patient agreement.', error);
        this.snackbar.open('Failed to download patient agreement. Please try again later.', 'Close', { duration: 5000 });
        this.isDownloading = false;
        return [];
      }))
      .subscribe((base64) => {
        if (base64) {
          // Download the base64.
          const link = document.createElement('a');
          link.href = 'data:application/pdf;base64,' + base64;
          link.download = `Patient Agreement ${dayjs().format('DD-MMM-YYYY HH-mm')}.pdf`;
          link.click();
        }
        this.isDownloading = false;
      });
  }

  public isValid() {
    return this.patientControl.get('signaturePatientId')?.value !== null && this.patientControl.get('signaturePatient')?.value !== null;
  }

  public isTouched() {
    return this.patientControl.get('signaturePatientId')?.touched || this.patientControl.get('signaturePatient')?.touched;
  }

  public uploadSignatureToServer() {
    // Should always have a signature file before calling this method.
    if (!this.signatureFile || !this.signatureFile.base64) return;

    this.fileService.uploadSignatureToServer(this.signatureFile, this.signatureFile.base64, this.signatureFile.filename)
      .then(response => {

        const signaturePatientIdControl = this.patientControl.get('signaturePatientId');
        const signaturePatientControl = this.patientControl.get('signaturePatient');

        // Add field to the form if it's not there (possible).
        if (!signaturePatientIdControl) {
          (<any>this.patientControl).addControl('signaturePatientId', new FormControl<number | null>(response.resource?.id || null));
        }
        if (!signaturePatientControl) {
          (<any>this.patientControl).addControl('signaturePatient', new FormControl<Resource | null>(response.resource || null));
        }

        setTimeout(() => {
          this.patientControl.get('signaturePatientId')?.setValue(response.resource?.id || null);
          this.patientControl.get('signaturePatient')?.setValue(response.resource || null);
          this.snackbar.open('Signature captured.', 'Close', { duration: 3000 });
        });
      })
      .catch(error => {
        console.error('Error uploading signature:', error);
        this.snackbar.open('Error uploading signature. Please try again.', 'Close', { duration: 3000 });
      });
  }

  private toFilename(): string {
    const patientId = this.patientControl.get('id')?.value;
    const timestamp = Date.now();
    return `patient-signature-${patientId}-${timestamp}.png`;
  }

}
